if (!customElements.get('ecoblue-sticky-buy')) {
  customElements.define(
    'ecoblue-sticky-buy',
    class EcoblueStickyBuy extends HTMLElement {
      connectedCallback() {
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.productInfo = this.closest('product-info');
        this.button = this.querySelector('.ecoblue-sticky-buy__button');
        this.label = this.querySelector('.ecoblue-sticky-buy__label');
        this.price = this.querySelector('.ecoblue-sticky-buy__price');
        this.mainSubmit = document.getElementById(this.dataset.mainSubmitId);
        this.priceRoot = document.getElementById(this.dataset.priceId);

        if (!this.productInfo || !this.button || !this.mainSubmit || !this.priceRoot) return;

        this.originalMainSubmitTabindex = this.mainSubmit.getAttribute('tabindex');
        this.originalMainSubmitAriaHidden = this.mainSubmit.getAttribute('aria-hidden');

        this.onScroll = this.scheduleVisibilityUpdate.bind(this);
        this.onResize = this.scheduleVisibilityUpdate.bind(this);
        this.onMediaChange = this.updateVisibility.bind(this);
        this.onButtonClick = this.forwardSubmit.bind(this);
        this.onVariantChange = this.handleVariantChange.bind(this);
        this.onPrimaryBlur = this.scheduleVisibilityUpdate.bind(this);

        this.button.addEventListener('click', this.onButtonClick);
        this.mainSubmit.addEventListener('blur', this.onPrimaryBlur);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        this.mobileQuery.addEventListener('change', this.onMediaChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.submitObserver = new MutationObserver(() => this.syncContent());
        this.submitObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });

        this.priceObserver = new MutationObserver(() => this.syncContent());
        this.priceObserver.observe(this.priceRoot, { childList: true, subtree: true, attributes: true });

        this.syncContent();
        this.updateVisibility();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onButtonClick);
        this.mainSubmit?.removeEventListener('blur', this.onPrimaryBlur);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        this.mobileQuery?.removeEventListener('change', this.onMediaChange);
        this.variantUnsubscriber?.();
        this.submitObserver?.disconnect();
        this.priceObserver?.disconnect();
        if (this.animationFrame) window.cancelAnimationFrame(this.animationFrame);
        this.restoreMainSubmitAccessibility();
      }

      handleVariantChange(event) {
        const eventSectionId = event?.data?.sectionId || event?.detail?.sectionId;
        if (eventSectionId && String(eventSectionId) !== String(this.dataset.sectionId)) return;
        this.syncContent();
      }

      syncContent() {
        if (!this.mainSubmit || !this.priceRoot) return;

        const mainLabel = this.mainSubmit.querySelector('span')?.textContent?.trim();
        if (mainLabel) this.label.textContent = mainLabel;

        const priceSelector = this.priceRoot.querySelector('.price--on-sale')
          ? '.price__sale .price-item--sale'
          : '.price__regular .price-item--regular';
        const currentPrice = this.priceRoot.querySelector(priceSelector)?.textContent?.trim();
        this.price.textContent = currentPrice ? `· ${currentPrice}` : '';

        this.button.disabled = this.mainSubmit.disabled || this.mainSubmit.getAttribute('aria-disabled') === 'true';
      }

      forwardSubmit() {
        if (this.button.disabled || !this.mainSubmit) return;
        this.mainSubmit.click();
      }

      scheduleVisibilityUpdate() {
        if (this.animationFrame) return;
        this.animationFrame = window.requestAnimationFrame(() => {
          this.animationFrame = null;
          this.updateVisibility();
        });
      }

      updateVisibility() {
        if (!this.mainSubmit || !this.productInfo || !this.mobileQuery?.matches) {
          this.hide();
          return;
        }

        const submitRect = this.mainSubmit.getBoundingClientRect();
        const sectionRect = this.productInfo.getBoundingClientRect();
        const hasScrolledPastMainSubmit = submitRect.bottom <= 0;
        const isStillWithinProductSection = sectionRect.bottom > (this.offsetHeight || 0);
        const mainSubmitCanBeReplaced = document.activeElement !== this.mainSubmit;

        if (hasScrolledPastMainSubmit && isStillWithinProductSection && mainSubmitCanBeReplaced) {
          this.show();
        } else {
          this.hide();
        }
      }

      show() {
        this.mainSubmit.setAttribute('tabindex', '-1');
        this.mainSubmit.setAttribute('aria-hidden', 'true');
        this.hidden = false;
        this.setAttribute('aria-hidden', 'false');
      }

      hide() {
        this.hidden = true;
        this.setAttribute('aria-hidden', 'true');
        this.restoreMainSubmitAccessibility();
      }

      restoreMainSubmitAccessibility() {
        if (!this.mainSubmit) return;

        if (this.originalMainSubmitTabindex === null) {
          this.mainSubmit.removeAttribute('tabindex');
        } else {
          this.mainSubmit.setAttribute('tabindex', this.originalMainSubmitTabindex);
        }

        if (this.originalMainSubmitAriaHidden === null) {
          this.mainSubmit.removeAttribute('aria-hidden');
        } else {
          this.mainSubmit.setAttribute('aria-hidden', this.originalMainSubmitAriaHidden);
        }
      }
    }
  );
}
